import "@minket/ui/globals.css";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouteError } from "./components/shell/route-error";
import { isUnauthorized, shouldRetry } from "./lib/api";
import { sessionKey } from "./lib/queries";
import { routeTree } from "./routeTree.gen";

/**
 * Queries retry only what might fix itself (`shouldRetry`), so a 501 or a 404 shows its state
 * at once. A 401 on any page query means the Access session ended: the session is fetched
 * again and the root shows the Access page.
 */
const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isUnauthorized(error) && query.queryKey[1] !== sessionKey[1]) {
        void queryClient.invalidateQueries({ queryKey: sessionKey });
      }
    },
  }),
  defaultOptions: { queries: { staleTime: 30_000, retry: shouldRetry } },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteError,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
